import { PixoSpatialVector } from './PixoSpatialVector';
import { ScaleInfo } from './../components/dialogs/scale-and-location-dialog/scale-and-location-dialog';
import * as turf from '@turf/turf';
import LatLonEllipsoidal, { Dms } from 'geodesy/latlon-ellipsoidal-vincenty';
import { ROICorners } from '../components/draw-pane/rectangle-roi';

export type TurfPoint = turf.helpers.Feature<turf.helpers.Point, { [name: string]: any; }>
export interface PixoPt {
  x: number;
  y: number;
  z?: number;
  convert: () => SpatialCoord;
  clone: () => PixoPt;
}
export interface SpatialCoord {
  lng: number;
  lat: number;
  accuracy?: number;
  timestamp?: number;
  altitude?: number;
  altitudeAccuracy?: number;
  heading?: number;
  speed?: number;
  convert: () => PixoPt;
  clone: () => SpatialCoord;
}
export interface PixoSpatialCoord {
  pixo: PixoPt;
  spatial: SpatialCoord;
  Project: () => void;
  UnProject: () => void;
  UpdateFromCoord: (coord: PixoSpatialCoord) => void;
  UpdateSpatial: (lng: number, lat: number, accuracy?: number, timestamp?: number,
    altitude?: number, altitudeAccuracy?: number, heading?: number, speed?: number) => void;
  UpdatePixo: (x: number, y: number, z?: number) => void;

  PostConvert?: () => {};
  Equals: (coord: PixoSpatialCoord) => boolean;
}



export type PixoSpatialProjectFunction = (spatialCoord: SpatialCoord) => PixoPt;
export type PixoSpatialUnprojectFunction = (pxPt: PixoPt) => SpatialCoord;
export type SpatialUnits = 'meters-cm' | 'meters-m' | 'meters-km' | 'meters-auto' | 'imperial-in' | 'imperial-ft' | 'imperial-yd' | 'imperial-mi' | 'imperial-auto';
export const SpatialUnitsPerMeter = {
  'cm': 100.0,
  'm': 1,
  'km': .001,
  'in': 39.3701,
  'ft': 3.28084,
  'yd': 1.09361,
  'mi': 0.000621371
}
export class PixoSpatial {
  private pProjectHandler = null;
  private pUnprojectHandler = null;
  private pIsDetatched = true;

  readonly detatched: boolean;
  get detached() {
    return this.pIsDetatched;
  }
  constructor() { }
  static SpatialDistanceChange(distance: number, units: SpatialUnits, targetUnits: SpatialUnits): null | { distance: number, units: SpatialUnits } {
    let meterDistance = 0;
    switch (units) {
      case 'imperial-auto':
      case 'meters-auto':
        return null;
    }
    return { distance: (distance * SpatialUnitsPerMeter[targetUnits.split('-')[1]]) / SpatialUnitsPerMeter[units.split('-')[1]], units: units }
  }

  Project(spatialCoord: SpatialCoord): PixoPt {
    return this.pProjectHandler(spatialCoord);
  }

  UnProject(pxPt: PixoPt): SpatialCoord {
    return this.pUnprojectHandler(pxPt);
  }
  IsPixoSpatialCoord(val: any): boolean {
    return !!val.pixo;
  }
  PixoSpatialCoord(startItem: SpatialCoord | PixoPt): PixoSpatialCoord {
    let coord: PixoSpatialCoord;

    if (startItem.hasOwnProperty('lng')) {
      coord = {
        pixo: (startItem as SpatialCoord).convert(),
        spatial: startItem as SpatialCoord,
        Project: null,
        UnProject: null,
        UpdateFromCoord: null,
        UpdateSpatial: null,
        UpdatePixo: null,
        Equals: null
      };
    } else {
      coord = {
        pixo: startItem as PixoPt,
        spatial: (startItem as PixoPt).convert(),
        Project: null,
        UnProject: null,
        UpdateFromCoord: null,
        UpdateSpatial: null,
        UpdatePixo: null,
        Equals: null
      };
    }
    coord.Project = () => {
      coord.spatial = coord.pixo.convert();
    };
    coord.UnProject = () => {
      coord.pixo = coord.spatial.convert();
    };
    coord.UpdateFromCoord = (src: PixoSpatialCoord) => {
      coord.UpdateSpatial(src.spatial.lng, src.spatial.lat, src.spatial.accuracy, src.spatial.timestamp, coord.spatial.altitude, coord.spatial.altitudeAccuracy, coord.spatial.heading, coord.spatial.speed);
    }
    coord.UpdateSpatial = (
      lng: number, lat: number, accuracy?: number, timestamp?: number,
      altitude?: number, altitudeAccuracy?: number, heading?: number, speed?: number, mostAccurate: boolean = false
    ) => {
      if (mostAccurate === true) {
        if (coord.spatial.accuracy && coord.spatial.accuracy > 0) {
          if ((accuracy > coord.spatial.accuracy) && (accuracy > 0)) {
            return;
          }
        }
      }


      coord.spatial.lng = lng;
      coord.spatial.lat = lat;
      if (!!accuracy) {
        coord.spatial.accuracy = accuracy;
      }
      if (!!timestamp) {
        coord.spatial.timestamp = timestamp;
      }
      if (!!altitude) {
        coord.spatial.altitude = altitude;
      }
      if (!!altitudeAccuracy) {
        coord.spatial.altitudeAccuracy = altitudeAccuracy;
      }
      if (!!heading) {
        coord.spatial.heading = heading;
      }
      if (!!speed) {
        coord.spatial.speed = speed;
      }
      coord.UnProject();
      if (!!coord.PostConvert) {
        coord.PostConvert();
      }
    };
    coord.UpdatePixo = (x: number, y: number, z?: number) => {
      coord.pixo.x = x;
      coord.pixo.y = y;
      if (!!z) {
        coord.pixo.z = z;
      }
      coord.Project();
    };
    coord.Equals = (testCoord: PixoSpatialCoord): boolean => {
      if (coord.pixo.x === testCoord.pixo.x) {
        if (coord.pixo.y === testCoord.pixo.y) {
          return true;
        }
      }
      return false;
    };
    return coord;
  }
  PixoPt(ptObj: any, xProp: string = 'x', yProp: string = 'y', zProp: string = 'z'): PixoPt {
    const pixoPt: PixoPt = {
      x: !!ptObj[xProp] ? Number(ptObj[xProp]) : 0,
      y: !!ptObj[yProp] ? Number(ptObj[yProp]) : 0,
      convert: null,
      clone: null
    };
    pixoPt.convert = (): SpatialCoord => {
      return this.UnProject(pixoPt);
    };
    pixoPt.clone = (): PixoPt => {
      return this.PixoPt({ x: pixoPt.x, y: pixoPt });
    }
    if (!!pixoPt[zProp]) {
      pixoPt.z = pixoPt[zProp];
    }
    return pixoPt;
  }
  SpatialCoord(coord: any, lngProp: string = 'lng', latProp: string = 'lat'): SpatialCoord {
    const spatialCoord: SpatialCoord = {
      lng: !!coord[lngProp] ? Number(coord[lngProp]) : 0,
      lat: !!coord[latProp] ? Number(coord[latProp]) : 0,
      convert: null,
      clone: null
    };
    spatialCoord.convert = (): PixoPt => {
      return this.Project(spatialCoord);
    };
    spatialCoord.clone = (): SpatialCoord => {
      return this.SpatialCoord({ lng: spatialCoord.lng, lat: spatialCoord.lat });
    }
    return spatialCoord;
  }
  SpatialDistance(coordA: SpatialCoord, coordB: SpatialCoord, units: SpatialUnits): ScaleInfo {
    const coordALatLon: LatLonEllipsoidal = new LatLonEllipsoidal(coordA.lat, coordA.lng);
    const coordBLatLon: LatLonEllipsoidal = new LatLonEllipsoidal(coordB.lat, coordB.lng);
    const length = coordALatLon.distanceTo(coordBLatLon);
    const unitInfo = units.split('-');
    switch (unitInfo[0]) {
      case 'imperial':
        switch (unitInfo[1]) {
          case 'auto':
            const inchLen = { length: length * SpatialUnitsPerMeter['in'], units: 'imperial-in' };
            if (inchLen.length < 12) return { length: inchLen.length, units: 'imperial-in', precisionType: 'auto' };
            const ftLen = length * SpatialUnitsPerMeter['ft'];
            if (ftLen < 5280) return { length: length * SpatialUnitsPerMeter['ft'], units: 'imperial-ft', precisionType: 'auto' };
            return { length: length * SpatialUnitsPerMeter['mi'], units: 'imperial-mi', precisionType: 'auto' };
          default:
            return { length: Math.round(length * SpatialUnitsPerMeter[unitInfo[1]]), units: units, precisionType: 'auto' }
        }
      case 'meters':
        switch (unitInfo[1]) {
          case 'auto':
            if (length < 1) {
              return { length: Math.round(length * SpatialUnitsPerMeter['cm']), units: 'meters-cm', precisionType: 'auto' };
            }
            if (length < 1000) {
              return { length: Math.round(length * SpatialUnitsPerMeter['m']), units: 'meters-m', precisionType: 'auto' };
            }
            if (length > 1000) {
              return { length: Math.round(length * SpatialUnitsPerMeter['km']), units: 'meters-km', precisionType: 'auto' };
            }
          default:
            return { length: Math.round(length * SpatialUnitsPerMeter[unitInfo[1]]), units: units, precisionType: 'auto' };
        }
    }
    return { length: 0, units: units, precisionType: 'auto' };
  }
  PointAtDistance(lng: number, lat: number, distance: number, units: SpatialUnits): PixoPt {
    if (distance === 0) {
      return this.SpatialCoord({ lng: lng, lat: lat }).convert();

    }
    const kmDistance = (distance / SpatialUnitsPerMeter[units.split('-').pop()]) / 1000;
    const turfPt = turf.destination<'testPt'>(turf.point([lng, lat]), kmDistance, -90, {});
    return this.SpatialCoord({ lng: turfPt.geometry.coordinates[0], lat: turfPt.geometry.coordinates[1] }).convert();
  }
  PointAtBearing(coord: PixoSpatialCoord, distance: number, bearing: number): PixoSpatialCoord {
    if (distance === 0) {
      return coord;
    }
    const kmDistance = distance / 1000;
    const turfPt = turf.destination(this.CoordToTurf(coord), kmDistance, bearing, { units: 'kilometers' });
    return this.TurfToCoord(turfPt);
  }
  MakeVector(pt1: PixoSpatialCoord, pt2: PixoSpatialCoord) {
    const vector = new PixoSpatialVector(this, pt1, pt2);
    return vector;
  }
  Bearing(pt1: PixoSpatialCoord, pt2: PixoSpatialCoord) {
    const vector = this.MakeVector(pt1, pt2);
    return vector.bearingTo;
  }
  CoordToTurf(coord: PixoSpatialCoord): TurfPoint {
    return turf.point([coord.spatial.lng, coord.spatial.lat]);
  }
  CoordsToTurfLine(coord1: PixoSpatialCoord, coord2: PixoSpatialCoord) {
    return turf.lineString([[coord1.spatial.lng, coord1.spatial.lat], [coord2.spatial.lng, coord2.spatial.lat]]);
  }
  TurfToCoord(turf) {
    const [lng, lat] = turf.geometry.coordinates;
    return this.PixoSpatialCoord(this.SpatialCoord({ lng: Number(lng), lat: Number(lat) }));
  }
  CoordDistance(coord1: PixoSpatialCoord, coord2: PixoSpatialCoord): number {
    return this.MakeVector(coord1, coord2).distance;
  }
  CoordIntersect(line1From: PixoSpatialCoord, line1To: PixoSpatialCoord, line2From: PixoSpatialCoord, line2To: PixoSpatialCoord): PixoSpatialCoord | undefined {
    const v1 = this.MakeVector(line1From, line1To);
    const v2 = this.MakeVector(line2From, line2To);
    return v1.Intersect(v2);
  }
  GetNearestCoord(lineFrom: PixoSpatialCoord, lineTo: PixoSpatialCoord, fromPt: PixoSpatialCoord) {
    const vector = this.MakeVector(lineFrom, lineTo);
    const coord = vector.GetNearestPoint(fromPt);
    return coord;
  }
  GetNearestInfiniteCoord(lineFrom: PixoSpatialCoord, lineTo: PixoSpatialCoord, fromPt: PixoSpatialCoord) {
    const vector = this.MakeVector(lineFrom, lineTo);
    const coord = vector.GetNearestInfinitePoint(fromPt);
    return coord;
  }
  GetRotatedCoord(rotCoord: PixoSpatialCoord, aboutCoord: PixoSpatialCoord, degrees: number) {
    const psVector = this.MakeVector(rotCoord, aboutCoord);
    const vector = psVector.ToVector();
    vector.rotateToRelative(degrees);
    const coord = psVector.MeterCoordToCoord(vector.to);
    return coord;
  }
  GetRotatedToCoord(center: PixoSpatialCoord, rotCoord: PixoSpatialCoord, degrees: number) {
    const psVector = this.MakeVector(center, rotCoord);
    const vector = psVector.ToVector();
    vector.rotateTo(degrees);
    return psVector.MeterCoordToCoord(vector.to);
  }

  RotateCoords(rotCoord: PixoSpatialCoord, aboutCoord: PixoSpatialCoord, degrees: number) {
    const vector = this.MakeVector(aboutCoord, rotCoord);
    const coord = vector.RotateToRelative(degrees).to;
    rotCoord.UpdateSpatial(coord.spatial.lng, coord.spatial.lat);
  }
  GetPointsInROI(roiCorners: ROICorners, points: Array<any>): Array<any> {
    let fromLng = roiCorners.from.spatial.lng;
    let fromLat = roiCorners.from.spatial.lat;
    let toLng = roiCorners.to.spatial.lng;
    let toLat = roiCorners.to.spatial.lat;
    const minLng = Math.min(fromLng, toLng);
    const maxLng = Math.max(fromLng, toLng);
    const minLat = Math.min(fromLat, toLat);
    const maxLat = Math.max(fromLat, toLat);
    const roiPoints = points.filter((point: any) => {
      const spatial = point.coord?.spatial;
      if (!spatial) return [];
      const lng = spatial.lng;
      const lat = spatial.lat;
      if (lng < minLng) return false;
      if (lng > maxLng) return false;
      if (lat < minLat) return false;
      if (lat > maxLat) return false;
      return true;
    });
    return roiPoints;
  }
  GetPointsInPolygon(polygon: turf.GeoJSONObject, points: Array<any>): Array<any> {
    const turfPoints: TurfPoint[] = [];
    let lookup: any = [];
    points.every((point: any) => {
      const coord = point.coord ? point.coord : point;
      if (!lookup[coord.spatial.lng]) {
        lookup[coord.spatial.lng] = [];
      }
      lookup[coord.spatial.lat] = point;
      turfPoints.push(this.CoordToTurf(coord));
      return true;
    })
    const coords = [];
    turf.pointsWithinPolygon(turf.featureCollection(turfPoints), polygon as turf.Polygon).features.every(
      (point: any) => {
        const [lng, lat] = point.geometry.coordinates;
        coords.push(lookup[lng][lat]);
        return true;
      });
    lookup = undefined;
    return coords;
  }
  SortByDistance(coord: PixoSpatialCoord, points: Array<any>) {
    if (points.length && !!points[0].coord) {
      points.sort((a, b) => {
        return this.CoordDistance(coord, a.coord) - this.CoordDistance(coord, b.coord);
      });
    } else {
      points.sort((a, b) => {
        return this.CoordDistance(coord, a) - this.CoordDistance(coord, b);
      });
    }
  }
  Attach(
    project: PixoSpatialProjectFunction,
    unproject: PixoSpatialUnprojectFunction
  ) {
    this.pProjectHandler = project;
    this.pUnprojectHandler = unproject;
    this.pIsDetatched = true;
  }
  Detatch() {
    this.pProjectHandler = null;
    this.pUnprojectHandler = null;
    this.pIsDetatched = true;
  }

}
